import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import { CheckCircle as SuccessIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { IMaskInput } from 'react-imask';
import api from '../../services/api';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { useBranding } from '../../context/BrandingContext';
import Logo from '../../components/Logo';

interface IFormInput {
  name: string;
  email: string;
  phone: string;
}

interface IAffiliateData {
  affiliate: {
    name: string;
    code: string;
  };
  discount: {
    value: number;
    type: 'fixed' | 'percentage';
  };
}

interface PhoneMaskProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const PhoneMaskCustom = React.forwardRef<HTMLInputElement, PhoneMaskProps>(
  function PhoneMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="(00) 00000-0000"
        inputRef={ref}
        onAccept={(value: string) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  }
);

const schema = yup.object({
  name: yup.string().required('Nome é obrigatório'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  phone: yup.string().required('Telefone é obrigatório'),
});

const ClientForm: React.FC = () => {
  const theme = useTheme();
  const { branding } = useBranding();
  const { affiliateCode } = useParams<{ affiliateCode: string }>();
  const [affiliateData, setAffiliateData] = useState<IAffiliateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Atualiza o título e favicon da página
  useDocumentMeta();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const fetchAffiliate = async () => {
      if (!affiliateCode) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/users/by-code/${affiliateCode}`);
        setAffiliateData(response.data.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliate();
  }, [affiliateCode]);

  const onSubmit = async (data: IFormInput) => {
    try {
      setSubmitting(true);
      setError('');
      await api.post('/clients/register', {
        ...data,
        affiliate_code: affiliateCode,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao realizar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDiscount = () => {
    if (!affiliateData?.discount) return '';
    const { value, type } = affiliateData.discount;
    if (value === 0) return '';
    return type === 'fixed' ? `R$ ${value.toFixed(2)}` : `${value}%`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo height={48} />
            </Box>
            <Typography variant="h5" color="error" gutterBottom>
              Link inválido
            </Typography>
            <Typography color="text.secondary">
              Este link de indicação não é válido ou expirou.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <SuccessIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Cadastro realizado!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Em breve nossa equipe entrará em contato com você.
            </Typography>
            {affiliateData?.discount && affiliateData.discount.value > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Você receberá um desconto de {formatDiscount()} por ter sido indicado por{' '}
                {affiliateData.affiliate.name}!
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Mesmo gradiente da AppBar (definido no tema em App.tsx)
  const referralCardBg = `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`;
  const textMutedOnCard = 'rgba(255,255,255,0.8)';
  const textFieldOnDarkSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.98)',
      borderRadius: 1,
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%', background: referralCardBg, color: '#fff' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo height={48} color="#fff" />
          </Box>

          {affiliateData && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" sx={{ color: textMutedOnCard }}>
                Você foi indicado por
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
                {affiliateData.affiliate.name}
              </Typography>
              {affiliateData.discount.value > 0 && (
                <Chip
                  label={`Desconto de ${formatDiscount()}`}
                  sx={{
                    mt: 1,
                    bgcolor: `${branding.colors.primary}33`,
                    color: branding.colors.primary,
                    fontWeight: 600,
                    border: `1px solid ${branding.colors.primary}`,
                  }}
                />
              )}
            </Box>
          )}

          <Typography variant="body2" align="center" sx={{ mb: 3, color: textMutedOnCard }}>
            Preencha seus dados para continuar
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Nome completo"
              margin="normal"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              sx={textFieldOnDarkSx}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              sx={textFieldOnDarkSx}
            />

            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Telefone"
                  margin="normal"
                  placeholder="(00) 00000-0000"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  InputProps={{
                    inputComponent: PhoneMaskCustom as any,
                  }}
                  sx={textFieldOnDarkSx}
                />
              )}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              disabled={submitting}
              sx={{ mt: 3 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClientForm;
